import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import RemoteGame, { OnlineSession } from "../play/game/RemoteGame";
import { useAuth } from "../../shared/auth";
import { RANKED_BY_SLUG } from "../../shared/rating/ranked";
import { loadLocalPlayer } from "../../shared/local-players";
import FindingCard from "./Finding";
import FindingSeats from "./FindingSeats";
import { FoundBanner, FoundGrid, FOUND_BANNER_MS, FOUND_GRID_MS, type Seat } from "./Found";
import PlayNowGame from "../modes/PlayNowGame";
import musicController from "../../audio/music-controller";
import { fmtClock } from "../lobby/lobby";
import modes from "../modes/modes.module.scss";
import s from "./ranked.module.scss";

// A ranked mode's page: /ranked/duel | /ranked/ffa. Like a box's page
// (OnlineMatchPage) the whole session lives here, but the waiting room is
// different: the search on its own (a card, or the six seats filling), with
// "Play while you wait" to play Play Now meanwhile, and when the match lands
// MS Battle's banner (1v1) or starting grid (6 FFA) slams in, no Accept, then
// the countdown takes over. (There used to be a practice round under the card;
// Play while you wait took its place.)

// The hand-off, from match_start: the card flips to "Target acquired!" and
// holds FOUND_CARD_MS; then, over SWAP_MS, the
// search view scales down and fades while the banner / grid slams in, and the
// match's arena fades in slowly behind it (GAME_IN_MS); the banner plays out
// and leaves. The 3-2-1 starts a beat after that: the server sizes a ranked
// match's first countdown to this sequence (edge-server PUBLIC_ROOMS
// openingSecs).
const FOUND_CARD_MS = 2000;
const SWAP_MS = 500;
// The arena's fade-in behind the banner, from the swap.
const GAME_IN_MS = 3000;
// The banner / grid start leaving this long before they are gone.
const LEAVE_MS = 500;

export default function RankedMatchPage() {
  const params = useParams();
  const navigate = useNavigate();
  const meta = RANKED_BY_SLUG[(params.mode || "").toLowerCase()] || null;
  const { user, loading: authLoading } = useAuth();
  // Dev: ?hold=1 stays on the finding screen forever (no socket, no sign-in
  // needed) and ?hold=found freezes the match-found banner / grid on mock
  // seats, so both can be worked on in place. Linked from the Admin page.
  const hold = new URLSearchParams(window.location.search).get("hold");
  const held = hold === "1" || hold === "found";

  // Ranked needs an account: a guest lands back on the start page (the strip
  // says why). Decided once the session check has settled.
  useEffect(() => {
    if (held) return;
    if (!meta || (!authLoading && !user)) navigate("/", { replace: true });
  }, [meta, authLoading, user, navigate, held]);

  // The match id in the URL (?m=12 -> "public:ranked-duel#12"), as the boxes do:
  // a reload reconnects into the same match. Play again bumps joinKey.
  const [searchParams, setSearchParams] = useSearchParams();
  const [joinKey, setJoinKey] = useState(0);
  const roomId = useMemo(() => {
    if (!meta) return "";
    if (joinKey > 0) return meta.room;
    const m = (searchParams.get("m") || "").trim();
    return /^[A-Za-z0-9_-]{1,32}$/.test(m) ? meta.room + "#" + m : meta.room;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinKey, meta]);
  const onRoomInstance = useCallback((instance: string) => {
    const hash = instance.indexOf("#");
    if (hash <= 0) return;
    const next = new URLSearchParams(window.location.search);
    next.set("m", instance.slice(hash + 1));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [session, setSession] = useState<OnlineSession | null>(null);
  const waiting = !session || session.phase === "connecting" || session.phase === "waiting";
  // When this search started, for the card's clock.
  const [since, setSince] = useState(() => Date.now());
  // The held screen's clock for its mock roster (see waitSeats).
  const [holdNow, setHoldNow] = useState(() => Date.now());
  useEffect(() => {
    if (!held) return;
    const h = setInterval(() => setHoldNow(Date.now()), 500);
    return () => clearInterval(h);
  }, [held]);

  // Only the signed-in player on this device sits in a ranked match: a couch
  // guest would be bounced by the server.
  const [localPlayers] = useState(() => {
    const me = loadLocalPlayer(0);
    return me ? [me] : undefined;
  });

  // The hand-off: the first time the session leaves "waiting", the card flips
  // to found for a beat, then the banner or grid plays over the match's
  // opening countdown and leaves.
  type Found = "card" | "swap" | "stage" | "leaving" | null;
  const [found, setFound] = useState<Found>(null);
  const seatsRef = useRef<Seat[]>([]);
  const youRef = useRef<string[]>([]);
  const shownRef = useRef(false);
  useEffect(() => {
    if (waiting || shownRef.current || !session) return;
    shownRef.current = true;
    seatsRef.current = (session.players || []).map((p: any) => ({ id: p.id, name: p.name, colorId: p.colorId, flag: p.flag ?? null }));
    youRef.current = session.youIds || [];
    setFound("card");
    musicController.play("match-found");
    const total = meta && meta.mode === "ffa" ? FOUND_GRID_MS : FOUND_BANNER_MS;
    const t0 = setTimeout(() => setFound("swap"), FOUND_CARD_MS);
    const t1 = setTimeout(() => setFound("stage"), FOUND_CARD_MS + SWAP_MS);
    const t2 = setTimeout(() => setFound("leaving"), FOUND_CARD_MS + total - LEAVE_MS);
    const t3 = setTimeout(() => setFound(null), FOUND_CARD_MS + total);
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  const leave = useCallback(() => navigate("/"), [navigate]);
  // "Play while you wait": Play Now while the search runs; it gives way the
  // moment the match is found.
  const [playNow, setPlayNow] = useState(false);
  const [, tickNow] = useState(0);
  useEffect(() => {
    if (!playNow) return;
    const h = setInterval(() => tickNow((n) => n + 1), 500);
    return () => clearInterval(h);
  }, [playNow]);
  useEffect(() => { if (found === "stage") setPlayNow(false); }, [found]);
  const playAgain = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete("m");
    setSearchParams(next, { replace: true });
    setSession(null);
    setSince(Date.now());
    shownRef.current = false;
    setFound(null);
    setJoinKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!meta || (!user && !held)) return null;

  // The held banner's mock seats: you and five named opponents, no ratings
  // known (eggs), your seat first.
  const HOLD_SEATS: Seat[] = [
    { id: "you", name: localPlayers?.[0]?.name || "You", colorId: localPlayers?.[0]?.colorId || "blue", flag: localPlayers?.[0]?.flag ?? "se" },
    { id: "o1", name: "Kurvenkönig", colorId: "cyan", flag: "de" }, { id: "o2", name: "Zatacka_Zofia", colorId: "green", flag: "pl" },
    { id: "o3", name: "TightTurner", colorId: "yellow", flag: "gb" }, { id: "o4", name: "Slip_n_Slide", colorId: "purple", flag: "nl" }, { id: "o5", name: "InkTrail", colorId: "orange", flag: "cz" },
  ];
  // A roster that lands after match_start (a seat filled at launch) still
  // reaches the banner: while it plays, a fuller roster replaces the one taken
  // at the start.
  if (!held && found && session && (session.players || []).length > seatsRef.current.length) {
    seatsRef.current = session.players.map((p: any) => ({ id: p.id, name: p.name, colorId: p.colorId, flag: p.flag ?? null }));
  }
  const seats = held ? HOLD_SEATS : seatsRef.current;
  const you = held ? ["you"] : youRef.current;
  // The search's seats (6 FFA): who is in the room so far, you first; before the
  // socket has seated anyone, just you. The held screen shows three in.
  // Before the socket has seated anyone, just you (a placeholder); once the
  // roster is in, its seats only, yours first (by seat id, else by your name
  // until the id is known, so you never show twice). The held screen lands
  // one more every three seconds, round and round.
  const waitSeats: Seat[] = held
    ? HOLD_SEATS.slice(0, 1 + Math.floor((holdNow - since) / 3000) % 6)
    : (() => {
        const list = (session?.players || []).map((p: any) => ({ id: p.id, name: p.name, colorId: p.colorId, flag: p.flag ?? null }));
        if (!list.length) return localPlayers ? [{ id: "you", name: localPlayers[0].name || "You", colorId: localPlayers[0].colorId || "blue", flag: (localPlayers[0] as any).flag ?? null }] : [];
        const ids = session?.youIds || [];
        const isMine = (p: Seat) => ids.length ? ids.includes(p.id) : !!localPlayers && p.name === (localPlayers[0].name || "You");
        return [...list.filter(isMine), ...list.filter((p) => !isMine(p))];
      })();
  const waitYou = held ? ["you"] : (session?.youIds?.length ? session.youIds : ["you"]);
  const me = seats.find((p) => you.includes(p.id)) || seats[0];
  const opp = seats.find((p) => !you.includes(p.id)) || seats[1];
  const ratings = session?.rankedRatings ?? null;
  // The search view stays up through the card's hold and the swap. It also
  // stays up on the one render between the session leaving "waiting" and the
  // effect above starting the sequence: unmounting it there would restart the
  // practice round for a frame (a fresh round, then the card).
  const showWait = held ? hold === "1" : (waiting || found === "card" || found === "swap" || !shownRef.current);
  const foundHold = found === "card" || found === "swap";
  const showStage = held ? hold === "found" : (found === "swap" || found === "stage" || found === "leaving");

  return (
    <div style={{ position: "fixed", inset: 0, background: "#070a12", overflowY: waiting ? "visible" : "auto" }}>
      {!held && (
        // hidden under the search view until the swap, then fading in slowly
        // behind the banner (opacity alone: it must not become a containing
        // block for the game's fixed stage)
        <div className="page-main page-main-game" style={{ opacity: waiting || found === "card" ? 0 : 1, transition: `opacity ${GAME_IN_MS}ms ease` }}>
          <RemoteGame key={joinKey} roomId={roomId} onSession={setSession} onRoomInstance={onRoomInstance} onLeave={leave} onPlayAgain={playAgain} localPlayers={localPlayers as any} />
        </div>
      )}

      {/* The search: a local round with the finding card as a bar. Stays up for a
          beat after the match is found (the card flips), then the banner takes over. */}
      {showWait && playNow && (
        <div className={modes.page} style={{ zIndex: 5 }}>
          {waiting ? (
            <PlayNowGame onLeave={() => setPlayNow(false)}>
              <div className={`${modes.banner} ${modes.bannerAcc}`}>
                <span>Searching Ranked {meta.mode === "ffa" ? "6 FFA" : "1 vs 1"} · <b>{fmtClock(Math.max(0, Math.floor((Date.now() - since) / 1000)))}</b></span>
                <button className={modes.bannerX} onClick={leave} aria-label="Cancel search">×</button>
              </div>
            </PlayNowGame>
          ) : (
            <div className={modes.cover}>
              <div className={modes.findCard}>
                <h3>Match found</h3>
                <div className={modes.pullRow}><span className={`${modes.spin} ${modes.spinAcc}`} />Joining {meta.mode === "ffa" ? "6 FFA" : "1 vs 1"}</div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* The search on its own: the card (a duel) or the seats (6 FFA), with
          "Play while you wait" for something to do meanwhile. */}
      {showWait && !playNow && (
        <div className={`${s.wait} ${found === "swap" ? s.waitOut : ""}`}>
          <div className={`${s.dock} ${s.dockAlone} ${meta.mode === "ffa" ? s.dockFfa : ""}`}>
            {meta.mode === "ffa"
              ? <FindingSeats seats={waitSeats} total={meta.players} youIds={waitYou} ratings={ratings} since={since} found={foundHold} onCancel={leave} />
              : <FindingCard since={since} found={foundHold} leaving={found === "swap"} onCancel={leave} className={s.dockBar} />}
            {/* the Royale's waiting screen's button: big, the whole width */}
            <button type="button" className={`${modes.waitPlay} ${s.dockPlay}`} onClick={() => { if (!held) setPlayNow(true); }} disabled={foundHold}>Play while you wait</button>
          </div>
        </div>
      )}

      {/* a duel is always the banner: without the opponent yet it waits for
          them, never the six-seat grid with one card in it */}
      {showStage && me && (
        meta.mode === "duel"
          ? opp && <FoundBanner me={me} opp={opp} ratings={ratings} leaving={found === "leaving"} />
          : <FoundGrid seats={seats} youIds={you} ratings={ratings} leaving={found === "leaving"} />
      )}
    </div>
  );
}
